import { EnrichedDCConfig, EnrichedLocation, Printer } from '@/models/EnrichedPrismicDocuments.ts';
import { Appointment } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import { CheckinStatus, CheckoutStatus } from '@/models/call-system/models/CallSystemUser.ts';

import { dispatchTicket, getExtAuthToken, printTicket as printTicketCallSystem } from '@/services/callSystemService.ts';
import { printPcsTicket, updatePcsTicket } from '@/services/printerService.ts';

import { usePrinterStore } from '@/stores/printerStore.ts';

import { getPvsPatientId, isEternoAppointment } from '@/utils/appointmentUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

import { getPatientName } from './patientUtils.ts';

export const loadPrinterToken = async (config: EnrichedDCConfig): Promise<string | undefined> => {
    let { printer_token } = usePrinterStore.getState();

    if (
        !printer_token?.token ||
        printer_token.instanceId !== config.instance_id ||
        Date.now() > printer_token.validUntil
    ) {
        const [success, new_token] = await getExtAuthToken(config.instance_id, config.is_cloud_printer_system);
        if (!success || !new_token) {
            return undefined;
        }

        printer_token = {
            token: new_token,
            validUntil: Date.now() + 3600000, // 1 hour
            instanceId: config.instance_id,
        };

        usePrinterStore.setState({ printer_token });
    }

    return printer_token?.token;
};

export const prepareCheckinData = (
    appointment: Appointment,
    location: EnrichedLocation,
    config: EnrichedDCConfig,
    ticket: string,
    checkin_status: CheckinStatus,
    checkout_status?: CheckoutStatus
): UserAppointmentDto => {
    const is_eterno_appointment = isEternoAppointment(appointment);

    const checkin_data: UserAppointmentDto = {
        group: location.key,
        number: ticket,
        name: getPatientName(appointment),
        user_id: is_eterno_appointment ? appointment.user_id : (getPvsPatientId(appointment) ?? appointment.user_id),
        location_key: location.key,
        appointment: {
            id: appointment.id,
            checkin_status: checkin_status,
            checkout_status: checkout_status,
        },
        instance_id: config.instance_id,
        appointment_id: appointment.id,
    };
    return checkin_data;
};

export const printTicket = async (
    config: EnrichedDCConfig,
    selected_printer: Printer | undefined
): Promise<[boolean, string | undefined]> => {
    const auth_token = await loadPrinterToken(config);
    if (!auth_token) return [false, undefined];

    let is_success: boolean = false;
    let ticket: number | undefined;

    const is_cloud_printer_system = config.is_cloud_printer_system;
    if (is_cloud_printer_system) {
        if (!selected_printer) return [false, undefined];
        [is_success, ticket] = await printTicketCallSystem(config.instance_id, auth_token, selected_printer.id);
    } else {
        const printerAddress =
            selected_printer?.ip_address ??
            config.pcs_printers?.[0]?.ip_address ??
            getTextFromRichTextField(config.patient_call_system_ip);
        if (!printerAddress) return [false, undefined];
        [is_success, ticket] = await printPcsTicket(auth_token, printerAddress);
    }

    if (is_success) {
        return [is_success, String(ticket)];
    } else {
        return [is_success, undefined];
    }
};

export const updatePcs = async (
    config: EnrichedDCConfig,
    selected_printer: Printer | undefined,
    ticket: string,
    stackId: string
): Promise<boolean> => {
    const auth_token = await loadPrinterToken(config);
    if (!auth_token) return false;
    let is_success: boolean;

    const is_cloud_printer_system = config.is_cloud_printer_system;
    if (is_cloud_printer_system) {
        is_success = await dispatchTicket(config.instance_id, auth_token, ticket, stackId);
    } else {
        const printerAddress =
            selected_printer?.ip_address ??
            config.pcs_printers?.[0]?.ip_address ??
            getTextFromRichTextField(config.patient_call_system_ip);
        if (!printerAddress) return false;
        is_success = await updatePcsTicket(auth_token, printerAddress, stackId, ticket);
    }

    return is_success;
};

export const generatePrinterKey = (id: string, ip_address: string): string => {
    return `${id}-${ip_address}`;
};
